import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from "framer-motion";
import { FONT, COLOR } from "../lib/tokens";
import { pickCookColor } from "../lib/cookColors";

// CookBanner — pinned "you've got a cook in progress" bar.
//
// Two states:
//   expanded  — full bar: kicker + title + step pill + timer + bar.
//               Tap anywhere = resume CookMode at the live step.
//               Swipe up    = collapse to mini-bar.
//               × button    = dismiss for this tab session.
//
//   collapsed — thin mini-bar: just the timer + progress bar.
//               Tap = expand back to full bar (one more tap = resume).
//
// Layout discipline:
//   The banner stays position:fixed for proper iOS safe-area handling,
//   BUT writes its current height into a CSS variable
//   `--cook-banner-h` on <html>. The App's tab-content wrapper reads
//   that variable as paddingTop, so the page below is pushed down by
//   the banner's exact height — no UI gets covered up.
//   The variable is removed when the banner hides, so non-cook
//   sessions stay full-height.
//
// Why fixed-with-spacer instead of position:sticky:
//   Sticky requires a single scroll container. The App's tab content
//   uses framer-motion AnimatePresence with cross-fade transforms
//   between tabs — sticky inside a transformed parent breaks. The
//   CSS-variable spacer is the cleanest cross-tab solution.

const EXPANDED_BODY_H = 84;  // body height (excl. safe-area-top)
// Collapsed bar height: needs to be a comfortable thumb target. Apple
// HIG says ≥44pt for tappable elements; below that the user has to
// "aim." 48 gives the timer + bar room to breathe AND keeps the bar
// reachable when collapsed under the iOS notch.
const COLLAPSED_BODY_H = 48;
// Swipe thresholds — generous so a gentle pull commits, since the
// gesture is only ever upward and the user has just one direction
// to be wrong about. Either crossing -16px of offset OR upward
// velocity faster than -120 px/s commits the collapse.
const SWIPE_THRESHOLD_PX = 16;
const SWIPE_THRESHOLD_VELOCITY = -120;

// Tab row height when present (multi-cook). Added on top of the
// banner body height when computing --cook-banner-h.
const TAB_ROW_H = 30;

export default function CookBanner({
  active,
  onResume,
  onDismiss,
  // Multi-cook switcher: when more than one cook is active, the
  // banner renders a numeric tab row at the top. Tap a tab → focuses
  // that cook (App.jsx-side state). Without these props (or with a
  // single cook), banner behaves identically to the legacy single-
  // cook version.
  cooks = null,
  focusedSessionId = null,
  onFocusCook = null,
}) {
  const { session, recipe, activeStep, timerEndsAt, dismissed } = active;
  const tabsList = Array.isArray(cooks) ? cooks : [];
  const showTabs = tabsList.length > 1;
  const focusedId = focusedSessionId || session?.id || null;

  // expanded vs collapsed. Always start expanded on a new session id.
  const [mode, setMode] = useState("expanded");
  useEffect(() => { setMode("expanded"); }, [session?.id]);

  // Drag-Y motion value drives THREE things:
  //   1. inner translate y
  //   2. outer paddingBottom — extends glass on pull-down
  //   3. outer clip-path inset bottom — shrinks glass on pull-up
  const dragY = useMotionValue(0);
  const dragExtraPx = useTransform(dragY, (y) => `${Math.max(0, y)}px`);
  const dragClipPath = useTransform(dragY, (y) =>
    y < 0 ? `inset(0 0 ${-y}px 0)` : "inset(0 0 0 0)",
  );

  // Natural banner height — driven by a motion value that the
  // INNER div binds to via `minHeight`. The spring tween below
  // animates this between expanded and collapsed targets when mode
  // flips; meanwhile the inner div's actual rendered height equals
  // this value (no overflow concerns because both bodies fit
  // within their target height). The variable formula uses the
  // SAME motion value so page content offset and inner height
  // stay locked in lockstep — when the text fades out and the box
  // shrinks, both the visible banner AND the page-content offset
  // descend together.
  const naturalH = useMotionValue(EXPANDED_BODY_H - 5);

  const title = session?.recipe_title || recipe?.title || "Active cook";
  // Step source: recipe.steps when resolved, else session.step_summary
  // (migration 0235 snapshot — survives recipe-resolution lag). Same
  // shape; renderer reads id/title/timer only.
  const stepsForBanner = (Array.isArray(recipe?.steps) && recipe.steps.length > 0)
    ? recipe.steps
    : (Array.isArray(session?.step_summary) ? session.step_summary : []);
  const totalSteps = stepsForBanner.length;
  const stepIndex = useMemo(() => {
    if (!activeStep || stepsForBanner.length === 0) return 0;
    // Telemetry can carry a `-s${stageIdx}` suffix on TimerStack
    // stages. Strip it before matching against the recipe's step ids
    // — without this the banner reads the active step as 0 even when
    // the cook is mid-stage on a real step.
    const baseStepId = String(activeStep.step_id || "").replace(/-s\d+$/, "");
    const idx = stepsForBanner.findIndex(s => String(s.id) === baseStepId);
    return idx >= 0 ? idx + 1 : 0;
  }, [activeStep, stepsForBanner]);

  // Live tick.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!timerEndsAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [timerEndsAt]);

  const remainingMs = timerEndsAt ? new Date(timerEndsAt).getTime() - now : null;
  const remainingSec = remainingMs != null ? Math.max(0, Math.ceil(remainingMs / 1000)) : null;
  const timerDone = remainingMs != null && remainingMs <= 0;
  const mins = remainingSec != null ? Math.floor(remainingSec / 60) : 0;
  const secs = remainingSec != null ? remainingSec % 60 : 0;
  const pulsing = remainingSec != null && remainingSec > 0 && remainingSec <= 10;

  const nominal = Number(activeStep?.nominal_seconds) || 0;
  const progressPct = nominal > 0 && remainingSec != null
    ? Math.max(0, Math.min(100, ((nominal - remainingSec) / nominal) * 100))
    : null;

  const visible = Boolean(session && !dismissed);

  // naturalH springs between expanded and collapsed targets when
  // mode changes — but the spring is FROZEN while isDragging is
  // true. Mid-drag mode flip (text unmounts) doesn't shrink the
  // box; the shrink to collapsed-natural happens AFTER release as
  // part of dragEnd's animate call. Banner stays at its current
  // height during the gesture, then settles to the final mode's
  // height on release.
  const isDragging = useRef(false);
  useEffect(() => {
    if (!visible) return undefined;
    if (isDragging.current) return undefined;
    const target = (mode === "expanded" ? EXPANDED_BODY_H : COLLAPSED_BODY_H) - 5;
    const controls = animate(naturalH, target, {
      type: "spring",
      stiffness: 700,
      damping: 38,
    });
    return () => controls.stop();
  }, [visible, mode, naturalH]);

  // Couple mode to dragY mid-drag. Symmetric thresholds at the
  // height-diff between expanded and collapsed:
  //   y < -DIFF: pulling up past where the collapsed banner's
  //              bottom would be → flip expanded → collapsed
  //   y >  DIFF: pulling down past the diff → flip collapsed →
  //              expanded (so user feels the banner unfold during
  //              the drag, before the threshold for opening cook)
  const HEIGHT_DIFF_THRESHOLD = EXPANDED_BODY_H - COLLAPSED_BODY_H;
  useEffect(() => {
    if (!visible) return undefined;
    const unsub = dragY.on("change", (y) => {
      if (y < -HEIGHT_DIFF_THRESHOLD) {
        setMode((prev) => (prev === "expanded" ? "collapsed" : prev));
      } else if (y > HEIGHT_DIFF_THRESHOLD) {
        setMode((prev) => (prev === "collapsed" ? "expanded" : prev));
      }
    });
    return unsub;
  }, [visible, dragY, HEIGHT_DIFF_THRESHOLD]);

  // Tracks the mode the gesture started in. From collapsed, a pull-
  // down should ONLY expand — never open cook mode — even if the
  // drag is fast/long enough that it flips to expanded mid-gesture.
  // Cook open requires starting from an already-expanded banner.
  const gestureStartModeRef = useRef(mode);

  // Push page content down via --cook-banner-h. Formula reads
  // naturalH + min(0, dragY). naturalH is the spring-controlled
  // box height that the inner div ALSO binds to (minHeight), so
  // the page-content offset and the visible banner shrink in
  // lockstep on mode flip.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (!visible) return undefined;
    const apply = () => {
      const baseH = naturalH.get();
      const y = dragY.get();
      const adjusted = Math.max(0, baseH + Math.min(0, y));
      // Add the tab row's height when multi-cook so the page below
      // the banner doesn't end up underneath the tabs.
      const totalH = adjusted + (showTabs ? TAB_ROW_H : 0);
      document.documentElement.style.setProperty("--cook-banner-h", `${totalH}px`);
    };
    apply();
    const unsubBase = naturalH.on("change", apply);
    const unsubDrag = dragY.on("change", apply);
    return () => {
      unsubBase();
      unsubDrag();
    };
  }, [visible, naturalH, dragY, showTabs]);

  // Real cleanup on banner unmount.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (visible) return undefined;
    document.documentElement.style.removeProperty("--cook-banner-h");
    return undefined;
  }, [visible]);

  const accent = timerDone ? COLOR.leaf : pulsing ? COLOR.rose : COLOR.gold;
  const accentDim = timerDone ? COLOR.leafDim : pulsing ? COLOR.roseDim : COLOR.goldDim;

  // Click-target restructure (kept from previous rebuild):
  //   motion.div is JUST animation/position — pointerEvents:none on it.
  //   Inner div carries onClick + style + pointerEvents:auto. This is
  //   the only reliable way to land iOS Safari taps when the surface
  //   has backdrop-filter (its own stacking context confuses framer-
  //   motion's gesture layer + sometimes plain onClick).
  const handlePrimaryTap = () => {
    // eslint-disable-next-line no-console
    console.log("[CookBanner] tap", {
      mode,
      hasRecipe: !!recipe,
      sessionSlug: session?.recipe_slug || null,
      sessionTitle: session?.recipe_title || null,
    });
    if (mode === "collapsed") {
      setMode("expanded");
      return;
    }
    onResume?.(recipe, session);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cook-banner"
          initial={{ y: -90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -90, opacity: 0 }}
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          style={{
            // Outer holds the GLASS surface. Always anchored to
            // top:0, paddingBottom grows with the drag offset so as
            // the inner banner content translates downward, the
            // outer's glass extends to fill the space above. Without
            // this, pulling down revealed bare page-background
            // between top:0 and the dragged inner.
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            maxWidth: 480,
            margin: "0 auto",
            zIndex: 120,
            pointerEvents: "none",
            background: "rgba(250,246,238,0.55)",
            backdropFilter: "blur(24px) saturate(160%)",
            WebkitBackdropFilter: "blur(24px) saturate(160%)",
            borderBottom: `1px solid ${accentDim}`,
            boxShadow: timerDone
              ? "0 6px 18px rgba(47,143,131,0.16), 0 1px 3px rgba(30,30,30,0.05)"
              : pulsing
              ? "0 6px 18px rgba(217,107,43,0.18), 0 1px 3px rgba(30,30,30,0.05)"
              : "0 6px 18px rgba(212,166,55,0.13), 0 1px 3px rgba(30,30,30,0.05)",
            paddingBottom: dragExtraPx,
            clipPath: dragClipPath,
            WebkitClipPath: dragClipPath,
            transition: "border-color 0.3s, box-shadow 0.3s",
          }}
        >
          {showTabs ? (
            <CookTabRow
              cooks={tabsList}
              focusedSessionId={focusedId}
              onFocus={onFocusCook}
            />
          ) : null}
          <motion.div
            // Drag handles both directions:
            //   ↑ swipe up   (expanded only) → collapse to thin bar
            //   ↓ pull down  (either mode)  → open the full CookMode
            //                                  view (calls onResume).
            // Spatial metaphor matches the open/close animation in
            // App.jsx: the cook view "lives" inside the banner; pull
            // DOWN to unfold it, swipe UP to fold it back in.
            drag="y"
            // top: tight constraint at -60, with a tiny elastic
            // (0.05) so dragSnapToOrigin still engages on release
            // (with elastic 0 the snap spring couldn't bring the
            // banner back). 0.05 means a 1000px finger overshoot
            // becomes only 50px past the constraint — effectively
            // a hard stop you can still recover from.
            // bottom: 200px direct-translation room, 0.55 elastic
            // for the generous open-cook pull-down gesture.
            dragConstraints={{ top: -60, bottom: 200 }}
            dragElastic={{ top: 0.05, bottom: 0.55 }}
            dragMomentum={false}
            dragSnapToOrigin
            // Stiffer snap-back. ~3x framer's default stiffness;
            // damping over critical (~49 is critical for stiffness
            // 600) so it lands fast and tight with no oscillation.
            dragTransition={{
              bounceStiffness: 600,
              bounceDamping: 55,
            }}
            onDragStart={() => {
              // Freeze naturalH at expanded for the duration of the
              // gesture — see the effect above.
              isDragging.current = true;
              // Capture the starting mode so onDragEnd knows whether
              // a pull-down should commit to "open cook" (only when
              // started from expanded) or just to "expand banner"
              // (when started from collapsed).
              gestureStartModeRef.current = mode;
            }}
            onDragEnd={(_e, info) => {
              // Release: unfreeze and animate naturalH to the
              // current mode's target.
              isDragging.current = false;
              const target = (mode === "expanded" ? EXPANDED_BODY_H : COLLAPSED_BODY_H) - 5;
              animate(naturalH, target, {
                type: "spring",
                stiffness: 700,
                damping: 38,
              });
              const offY = info.offset.y;
              const velY = info.velocity.y;
              // Pull-down to OPEN — wins regardless of mode. Threshold
              // is symmetric to the swipe-up collapse threshold; a
              // committed downward gesture (past px or fast velocity)
              // fires the resume.
              if (offY > SWIPE_THRESHOLD_PX || velY > -SWIPE_THRESHOLD_VELOCITY) {
                // Pull-down COMMIT. If the gesture started in
                // collapsed mode, force commit to expanded —
                // never open cook on the same drag, regardless
                // of distance/speed. Cook-open requires a
                // separate drag that STARTS in expanded mode.
                if (gestureStartModeRef.current === "collapsed") {
                  setMode("expanded");
                } else {
                  handlePrimaryTap();
                }
                return;
              }
              // Swipe-up to COLLAPSE — expanded mode only (collapsed
              // can't go shorter).
              if (mode === "expanded" && (offY < -SWIPE_THRESHOLD_PX || velY < SWIPE_THRESHOLD_VELOCITY)) {
                setMode("collapsed");
              }
            }}
            initial={false}
            // Bind y to the shared motionValue so the outer's
            // paddingBottom (driven by dragExtraPx) tracks the
            // inner's translate exactly. Snap-to-origin works the
            // same — release without committing animates dragY back
            // to 0, and the outer's padding follows.
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            onClick={handlePrimaryTap}
            role="button"
            tabIndex={0}
            aria-label={mode === "collapsed" ? "Expand cook banner" : `Resume cooking ${title}`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handlePrimaryTap(e); }
            }}
            style={{
              // Glass + border + shadow now live on the OUTER wrapper
              // so the surface extends up to top:0 regardless of how
              // far the inner is dragged. Inner is just the content
              // layer.
              y: dragY,
              minHeight: naturalH,
              overflow: "hidden",
              pointerEvents: "auto",
              // Top padding includes safe-area-inset-top so the
              // content sits below the notch — UNLESS the multi-cook
              // tab row is present above this body, in which case
              // those tabs already absorbed the safe-area inset and
              // we'd double-pad if we added it again here.
              padding: mode === "expanded"
                ? (showTabs
                    ? "11px 18px 12px"
                    : "calc(11px + env(safe-area-inset-top)) 18px 12px")
                : (showTabs
                    ? "8px 18px 8px"
                    : "calc(8px + env(safe-area-inset-top)) 18px 8px"),
              cursor: "pointer",
              userSelect: "none",
              // touch-action: none lets framer-motion claim touchmove
              // events instead of the browser handling them as a
              // vertical pan. Without this, swipe-to-collapse never
              // fires on iOS Safari — the page just scrolls instead.
              touchAction: "none",
              transition: "padding 0.18s ease",
            }}
          >
            <AnimatePresence mode="wait" initial={false}>
              {mode === "expanded" ? (
                <ExpandedBody
                  key="expanded"
                  title={title}
                  accent={accent}
                  timerDone={timerDone}
                  pulsing={pulsing}
                  remainingSec={remainingSec}
                  mins={mins}
                  secs={secs}
                  stepIndex={stepIndex}
                  totalSteps={totalSteps}
                  activeStep={activeStep}
                  progressPct={progressPct}
                  onDismiss={onDismiss}
                />
              ) : (
                <CollapsedBody
                  key="collapsed"
                  title={title}
                  accent={accent}
                  timerDone={timerDone}
                  pulsing={pulsing}
                  remainingSec={remainingSec}
                  mins={mins}
                  secs={secs}
                  progressPct={progressPct}
                  hasTimer={remainingSec != null}
                />
              )}
            </AnimatePresence>

            <style>{`
              @keyframes cookBannerPulse {
                0%, 100% { opacity: 1; }
                50%      { opacity: 0.6; }
              }
              /* Marquee: inner duplicates the text twice with a gap;
                 translating to -50% lands the second copy exactly
                 where the first copy started, so the loop is
                 seamless. linear easing per the emil-design-eng
                 marquee rule. */
              @keyframes cookBannerMarquee {
                0%   { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
            `}</style>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ExpandedBody({
  title, accent, timerDone, pulsing, remainingSec, mins, secs,
  stepIndex, totalSteps, activeStep, progressPct, onDismiss,
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.14 }}
    >
      {/* Row 1 — kicker, title, step chip. The dismiss × was
          intentionally removed: the user must enter CookMode and
          use the EXIT button to end a cook, so the banner can never
          be the place where a session quietly disappears. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
            color: accent, letterSpacing: "0.16em",
            marginBottom: 3, textTransform: "uppercase",
          }}>
            {timerDone ? "Timer's up — tap to finish" : "Cooking · tap to resume"}
          </div>
          <div style={{
            fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
            fontSize: 20, lineHeight: 1.15, color: COLOR.ink,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {title}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {totalSteps > 0 && stepIndex > 0 && (
            <span style={{
              fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
              color: COLOR.gold, letterSpacing: "0.12em",
              background: COLOR.goldDeep, border: `1px solid ${COLOR.goldDim}`,
              padding: "5px 9px", borderRadius: 5, whiteSpace: "nowrap",
            }}>
              STEP {stepIndex}/{totalSteps}
            </span>
          )}
        </div>
      </div>

      {/* Row 2 — countdown + step title + progress bar.
          Negative marginTop pulls the timer up toward the title's
          baseline — the title's line-height already provides
          breathing space below it, so a flat 0 marginTop reads as
          a wide gap. */}
      {remainingSec != null && (
        <div style={{ marginTop: -4 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <span style={{
              fontFamily: FONT.forma, fontSize: 28, fontWeight: 600,
              letterSpacing: "0.04em", fontVariantNumeric: "tabular-nums",
              color: accent,
              animation: pulsing ? "cookBannerPulse 1s ease-in-out infinite" : "none",
            }}>
              {timerDone ? "0:00" : `${mins}:${String(secs).padStart(2, "0")}`}
            </span>
            <MarqueeStepTitle text={activeStep?.step_title || "Timer"} />

          </div>
          {progressPct != null && (
            <div style={{
              marginTop: 8, height: 4, borderRadius: 2,
              background: COLOR.edge, overflow: "hidden",
            }}>
              <div style={{
                width: `${progressPct}%`, height: "100%",
                background: accent,
                transition: "width 0.5s linear, background 0.3s",
              }} />
            </div>
          )}
        </div>
      )}

      {/* Swipe-up affordance — small chevron near the bottom edge,
          subtle, only visible when there's room (i.e. expanded). */}
      <div style={{
        position: "absolute", bottom: 4, left: 0, right: 0,
        display: "flex", justifyContent: "center", pointerEvents: "none",
      }}>
        <span style={{
          width: 32, height: 3, borderRadius: 2,
          background: COLOR.border,
        }} />
      </div>
    </motion.div>
  );
}

function CollapsedBody({
  title, accent, timerDone, pulsing, remainingSec, mins, secs, progressPct, hasTimer,
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.14 }}
      style={{ display: "flex", alignItems: "center", gap: 10 }}
    >
      {/* Recipe label — Forma all-caps small kicker. Truncates on
          long titles so the timer + meter always have room. */}
      <span style={{
        fontFamily: FONT.forma,
        fontSize: 9,
        fontWeight: 600,
        color: COLOR.muted,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        flex: "0 1 auto",
        minWidth: 0,
        maxWidth: "40%",
      }}>
        {title}
      </span>
      {hasTimer ? (
        <>
          <span style={{
            fontFamily: FONT.forma, fontSize: 13, fontWeight: 600,
            letterSpacing: "0.04em", fontVariantNumeric: "tabular-nums",
            color: accent, flexShrink: 0,
            animation: pulsing ? "cookBannerPulse 1s ease-in-out infinite" : "none",
          }}>
            {timerDone ? "0:00" : `${mins}:${String(secs).padStart(2, "0")}`}
          </span>
          {/* Smaller progress bar — narrower so the title and timer
              have visual primacy, thinner so the bar reads as a
              status hint rather than a gauge. */}
          <div style={{
            flex: 1, height: 2, borderRadius: 1,
            background: COLOR.edge, overflow: "hidden",
            maxWidth: 120,
          }}>
            <div style={{
              width: `${progressPct ?? 0}%`, height: "100%",
              background: accent,
              transition: "width 0.5s linear, background 0.3s",
            }} />
          </div>
        </>
      ) : (
        <span style={{
          fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
          color: accent, letterSpacing: "0.14em", textTransform: "uppercase",
        }}>
          Tap to expand
        </span>
      )}
    </motion.div>
  );
}

// Marquee — auto-scrolls the truncated step title so the user can
// read the full text without expanding. Detects overflow on mount /
// text change; when the text fits, renders normally (no animation).
// When it overflows, duplicates the text inside the inner div and
// runs the cookBannerMarquee keyframe — translateX(-50%) lands the
// second copy exactly where the first copy started, so the loop is
// seamless. Speed scales with text length so a 200-char step doesn't
// blur past at the same rate as a 30-char step.
function MarqueeStepTitle({ text }) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [overflows, setOverflows] = useState(false);
  const [duration, setDuration] = useState(14);

  useEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;
    // Inner contains [text, gap, text]; halve scrollWidth to get the
    // length of one copy. If a single copy exceeds the wrapper width
    // we need to scroll.
    const oneCopyWidth = inner.scrollWidth / 2;
    const fits = oneCopyWidth <= wrap.clientWidth;
    setOverflows(!fits);
    // Pace the scroll at roughly 60px/sec — readable, not frantic.
    if (!fits) setDuration(Math.max(8, Math.round(oneCopyWidth / 60)));
  }, [text]);

  const baseTextStyle = {
    fontFamily: FONT.forma,
    fontSize: 11,
    fontWeight: 500,
    color: COLOR.muted,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };

  if (!overflows) {
    return (
      <span
        ref={(el) => {
          wrapRef.current = el;
          innerRef.current = el;
        }}
        style={{
          ...baseTextStyle,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {text}
      </span>
    );
  }

  return (
    <span
      ref={wrapRef}
      style={{
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        position: "relative",
        display: "block",
        // Soft fade at the trailing edge so the wrap-around isn't a
        // hard cut. Mask gradient applies only when scrolling.
        WebkitMaskImage: "linear-gradient(to right, transparent 0, black 12px, black calc(100% - 16px), transparent 100%)",
        maskImage: "linear-gradient(to right, transparent 0, black 12px, black calc(100% - 16px), transparent 100%)",
      }}
    >
      <span
        ref={innerRef}
        style={{
          ...baseTextStyle,
          display: "inline-block",
          willChange: "transform",
          animation: `cookBannerMarquee ${duration}s linear infinite`,
        }}
      >
        {text}
        <span style={{ display: "inline-block", width: "2.5em" }} aria-hidden />
        {text}
        <span style={{ display: "inline-block", width: "2.5em" }} aria-hidden />
      </span>
    </span>
  );
}

// CookTabRow — multi-cook tab strip rendered at the top of the
// banner when more than one cook is active. Numeric chips (1, 2, 3
// …) sized 22pt, color-coded by per-cook palette. Tap a tab → that
// cook's id is sent to onFocus, App.jsx flips focusedCookSessionId,
// useActiveCookSession picks that cook on the next render.
//
// NO emojis (per user preference). Tooltip / aria-label = recipe
// title for screen-reader / hover identification.
function CookTabRow({ cooks, focusedSessionId, onFocus }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        // Top padding pushes the tab chips BELOW the iOS status bar /
        // notch (safe-area-inset-top). Without this, the chips render
        // under the system clock + battery icons since the banner's
        // outer container is anchored to top:0 with the glass extending
        // up under the system chrome.
        padding: "calc(4px + env(safe-area-inset-top)) 12px 4px",
        // height includes safe-area so --cook-banner-h math is right.
        // We keep the static TAB_ROW_H constant for the page-offset
        // calc; visually the chips sit in the bottom 30pt of this box.
        alignItems: "center",
        pointerEvents: "auto",
        borderBottom: `1px solid ${COLOR.edge || "rgba(30,30,30,0.08)"}`,
      }}
    >
      {cooks.map((cook, i) => {
        const sid = cook?.session?.id;
        if (!sid) return null;
        const palette = pickCookColor(sid);
        const isFocused = sid === focusedSessionId;
        const title = cook?.recipe?.title
          || cook?.session?.recipe_title
          || `Cook ${i + 1}`;
        return (
          <button
            key={sid}
            type="button"
            onClick={() => onFocus?.(sid)}
            title={title}
            aria-label={`Switch to ${title}`}
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              border: `1.5px solid ${palette.ring}`,
              background: isFocused ? palette.ring : "transparent",
              color: isFocused ? "#FFFFFF" : palette.ring,
              fontFamily: FONT.mono,
              fontSize: 11,
              fontWeight: 800,
              cursor: "pointer",
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "0 0 auto",
            }}
          >
            {i + 1}
          </button>
        );
      })}
    </div>
  );
}
